package features.notifications.ui.modals

import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.HTMLElement
import AppState
import features.notifications.ui.utils.NotificationUtils
import shared.utils.TimeUtils

/**
 * Modal to display all notifications
 */
object NotificationListModal {
    fun render(root: HTMLElement) {
        root.append {
            div("fixed inset-0 z-[150] hidden opacity-0 scale-95 transition-all duration-300 flex items-start justify-center pt-20 px-4") {
                id = "notification-list-modal"
                
                div("bg-dark-800 rounded-2xl border border-dark-700 w-full max-w-lg max-h-[600px] flex flex-col shadow-2xl") {
                    attributes["onclick"] = "event.stopPropagation()"
                    
                    // Header
                    div("flex justify-between items-center p-6 border-b border-dark-700") {
                        h2("text-2xl font-bold") { +"Notifications" }
                        
                        div("flex items-center gap-3") {
                            // Mark all as read button
                            if (AppState.unreadNotificationCount > 0) {
                                button(classes = "text-sm text-brand-cyan hover:text-brand-teal transition font-medium") {
                                    attributes["onclick"] = "window.markAllNotificationsAsRead()"
                                    +"Mark all as read"
                                }
                            }
                            
                            // Close button
                            button(classes = "text-slate-400 hover:text-white text-2xl transition") {
                                onClickFunction = { shared.ui.ModalManager.close() }
                                +"✕"
                            }
                        }
                    }
                    
                    // Notification list (scrollable)
                    div("flex-1 overflow-y-auto") {
                        if (AppState.notifications.isEmpty()) {
                            // Empty state
                            div("flex flex-col items-center justify-center py-16 px-6 text-center") {
                                span("text-6xl mb-4") { +"🔔" }
                                h3("text-xl font-bold mb-2") { +"No notifications yet" }
                                p("text-slate-400") { +"You're all caught up! Notifications will appear here." }
                            }
                        } else {
                            // Notification items
                            div("divide-y divide-dark-700") {
                                AppState.notifications.forEach { notification ->
                                    renderNotificationItem(this, notification)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    
    private fun renderNotificationItem(parent: DIV, notification: features.notifications.domain.Notification) {
        parent.div {
            val bgClass = if (notification.isRead) "bg-dark-800/50" else "bg-dark-700"
            val textClass = if (notification.isRead) "text-slate-400" else "text-white"
            val hoverClass = "hover:bg-dark-600"
            
            classes = setOf(bgClass, hoverClass, "p-4", "cursor-pointer", "transition")
            
            // Click to mark as read
            if (!notification.isRead) {
                attributes["onclick"] = "window.markNotificationAsRead('${notification.id}')"
            }
            
            div("flex gap-3") {
                // Icon
                span("text-2xl flex-shrink-0") {
                    +NotificationUtils.getNotificationIcon(notification.type)
                }
                
                // Content
                div("flex-1 min-w-0") {
                    div("flex items-start justify-between gap-2 mb-1") {
                        h4("font-bold $textClass") {
                            +notification.title
                        }
                        
                        // Unread indicator (blue dot)
                        if (!notification.isRead) {
                            span("w-2 h-2 bg-brand-cyan rounded-full flex-shrink-0 mt-1.5") {}
                        }
                    }
                    
                    p("text-sm text-slate-400 mb-2") {
                        +notification.message
                    }
                    
                    // Timestamp
                    span("text-xs text-slate-500") {
                        +TimeUtils.formatRelativeTime(notification.createdAt)
                    }
                }
            }
        }
    }
}
